import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  CheckMigrationsInputSchema,
  type Config,
  type MigrationCheckResult,
  type MigrationInfo,
} from '../types/index.js';
import { findFiles, readText } from '../utils/fs.js';
import { getCurrentBranch, branchExists, getFileFromBranch, getDiff } from '../utils/git.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const checkMigrationsTool: Tool = {
  name: 'check_migrations',
  description: 'Analyze EF Core migrations for conflicts, ordering issues, and ModelSnapshot discrepancies between branches',
  inputSchema: {
    type: 'object',
    properties: {
      projectPath: {
        type: 'string',
        description: 'EF Core project path (default: auto-detect from config)',
      },
      branch: {
        type: 'string',
        description: 'Git branch to check (default: current branch)',
      },
      compareBranch: {
        type: 'string',
        description: 'Branch to compare against (e.g., "develop")',
      },
    },
  },
};

export async function handleCheckMigrations(
  args: unknown,
  config: Config
): Promise<string> {
  const input = CheckMigrationsInputSchema.parse(args);
  const projectPath = input.projectPath || config.smartstack.projectPath;

  logger.info('Checking migrations', { projectPath, branch: input.branch });

  const structure = await findSmartStackStructure(projectPath);

  if (!structure.migrations) {
    return '# Migration Check\n\n❌ No migrations folder found in the project.';
  }

  const result: MigrationCheckResult = {
    hasConflicts: false,
    migrations: [],
    conflicts: [],
    suggestions: [],
  };

  // Get current branch
  const currentBranch = input.branch || await getCurrentBranch(projectPath) || 'unknown';

  // Parse migrations
  result.migrations = await parseMigrations(structure.migrations, structure.root);

  // Check naming conventions
  checkNamingConventions(result, config);

  // Check chronological order
  checkChronologicalOrder(result);

  // If compare branch specified, check for conflicts
  if (input.compareBranch && await branchExists(input.compareBranch, projectPath)) {
    await checkBranchConflicts(
      result,
      structure,
      currentBranch,
      input.compareBranch,
      projectPath
    );
  }

  // Check ModelSnapshot
  await checkModelSnapshot(result, structure);

  // Update conflict status
  result.hasConflicts = result.conflicts.length > 0;

  // Generate suggestions
  generateSuggestions(result);

  return formatResult(result, currentBranch, input.compareBranch);
}

async function parseMigrations(
  migrationsPath: string,
  rootPath: string
): Promise<MigrationInfo[]> {
  const files = await findFiles('*.cs', { cwd: migrationsPath });
  const migrations: MigrationInfo[] = [];

  // Pattern: {context}_v{version}_{sequence}_{Description}.cs
  // Example: core_v1.0.0_001_CreateAuthUsers.cs
  const pattern = /^(\w+)_v(\d+\.\d+\.\d+)_(\d{3})_(.+)\.cs$/;

  for (const file of files) {
    const fileName = path.basename(file);

    // Skip designer and snapshot files
    if (fileName.includes('.Designer.') || fileName.includes('ModelSnapshot')) {
      continue;
    }

    const match = pattern.exec(fileName);

    if (match) {
      migrations.push({
        name: fileName.replace('.cs', ''),
        context: match[1],     // DbContext (core, extensions, etc.)
        version: match[2],     // Semver version (1.0.0, 1.2.0, etc.)
        sequence: match[3],    // Sequence number (001, 002, etc.)
        description: match[4],
        file: path.relative(rootPath, file),
        applied: true, // We'd need DB connection to check this
      });
    } else {
      // Non-standard naming
      migrations.push({
        name: fileName.replace('.cs', ''),
        context: 'Unknown',
        version: '0.0.0',
        sequence: '000',
        description: fileName.replace('.cs', ''),
        file: path.relative(rootPath, file),
        applied: true,
      });
    }
  }

  // Sort by version then sequence
  return migrations.sort((a, b) => {
    // Compare versions using semver logic
    const versionCompare = compareVersions(a.version, b.version);
    if (versionCompare !== 0) return versionCompare;
    return a.sequence.localeCompare(b.sequence);
  });
}

/**
 * Parse a semver string into numeric parts
 * Returns [major, minor, patch] or null if invalid
 */
function parseSemver(version: string): [number, number, number] | null {
  const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!match) {
    return null;
  }
  return [
    parseInt(match[1], 10),
    parseInt(match[2], 10),
    parseInt(match[3], 10),
  ];
}

/**
 * Compare two semver version strings
 * Returns: -1 if a < b, 0 if equal, 1 if a > b
 * Invalid versions are sorted to the end
 */
function compareVersions(a: string, b: string): number {
  const partsA = parseSemver(a);
  const partsB = parseSemver(b);

  // Handle invalid versions - sort them to the end
  if (!partsA && !partsB) return 0;
  if (!partsA) return 1;  // Invalid a goes after valid b
  if (!partsB) return -1; // Invalid b goes after valid a

  // Compare major.minor.patch
  for (let i = 0; i < 3; i++) {
    if (partsA[i] > partsB[i]) return 1;
    if (partsA[i] < partsB[i]) return -1;
  }
  return 0;
}

function checkNamingConventions(result: MigrationCheckResult, _config: Config): void {
  for (const migration of result.migrations) {
    if (migration.context === 'Unknown') {
      result.conflicts.push({
        type: 'naming',
        description: `Migration "${migration.name}" does not follow naming convention`,
        files: [migration.file],
        resolution: `Rename to format: {context}_v{version}_{sequence}_{Description} (e.g., core_v1.0.0_001_CreateAuthUsers)`,
      });
    }

    if (migration.version === '0.0.0') {
      result.conflicts.push({
        type: 'naming',
        description: `Migration "${migration.name}" missing version number`,
        files: [migration.file],
        resolution: `Use format: {context}_v{version}_{sequence}_{Description} where version is semver (1.0.0, 1.2.0, etc.)`,
      });
    }

    // Validate semver format
    if (migration.version !== '0.0.0' && !parseSemver(migration.version)) {
      result.conflicts.push({
        type: 'naming',
        description: `Migration "${migration.name}" has invalid version format: "${migration.version}"`,
        files: [migration.file],
        resolution: `Use semver format (major.minor.patch): 1.0.0, 1.2.0, 2.0.0, etc.`,
      });
    }
  }
}

function checkChronologicalOrder(result: MigrationCheckResult): void {
  const migrations = result.migrations.filter(m => m.context !== 'Unknown');

  for (let i = 1; i < migrations.length; i++) {
    const prev = migrations[i - 1];
    const curr = migrations[i];

    // Check if versions are in order (within same context)
    if (curr.context === prev.context) {
      const versionCompare = compareVersions(curr.version, prev.version);

      if (versionCompare < 0) {
        result.conflicts.push({
          type: 'order',
          description: `Migration "${curr.name}" (v${curr.version}) is versioned before "${prev.name}" (v${prev.version})`,
          files: [curr.file, prev.file],
          resolution: 'Reorder migrations or update version numbers',
        });
      }

      // Check for duplicate version with same sequence
      if (curr.version === prev.version && curr.sequence === prev.sequence) {
        result.conflicts.push({
          type: 'order',
          description: `Migrations "${curr.name}" and "${prev.name}" have same version and sequence`,
          files: [curr.file, prev.file],
          resolution: 'Use different sequence numbers (001, 002, etc.) for migrations in the same version',
        });
      }
    }
  }
}

async function checkBranchConflicts(
  result: MigrationCheckResult,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  currentBranch: string,
  compareBranch: string,
  projectPath: string
): Promise<void> {
  if (!structure.migrations) return;

  const migrationsRelPath = path.relative(projectPath, structure.migrations).replace(/\\/g, '/');

  // Get ModelSnapshot from both branches
  const snapshotFiles = await findFiles('*ModelSnapshot.cs', { cwd: structure.migrations });

  if (snapshotFiles.length > 0) {
    const snapshotRelPath = path.relative(projectPath, snapshotFiles[0]).replace(/\\/g, '/');

    const currentSnapshot = await readText(snapshotFiles[0]);
    const compareSnapshot = await getFileFromBranch(compareBranch, snapshotRelPath, projectPath);

    if (compareSnapshot && currentSnapshot !== compareSnapshot) {
      // Check if there are actual model differences
      const diff = await getDiff(compareBranch, currentBranch, snapshotRelPath, projectPath);

      if (diff) {
        result.conflicts.push({
          type: 'snapshot',
          description: `ModelSnapshot differs between "${currentBranch}" and "${compareBranch}"`,
          files: [snapshotRelPath],
          resolution: 'Rebase on target branch and regenerate migrations with: dotnet ef migrations add <Name>',
        });
      }
    }
  }

  // Check for migrations that exist in compare branch but not current
  const compareMigrations = await getFileFromBranch(
    compareBranch,
    migrationsRelPath,
    projectPath
  );

  if (compareMigrations) {
    // This would need more sophisticated parsing
    // For now, just note that comparison was done
    logger.debug('Branch comparison completed', { compareBranch });
  }
}

async function checkModelSnapshot(
  result: MigrationCheckResult,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never
): Promise<void> {
  if (!structure.migrations) return;

  const snapshotFiles = await findFiles('*ModelSnapshot.cs', { cwd: structure.migrations });

  if (snapshotFiles.length === 0) {
    result.conflicts.push({
      type: 'snapshot',
      description: 'No ModelSnapshot file found',
      files: [],
      resolution: 'Run: dotnet ef migrations add InitialCreate',
    });
    return;
  }

  if (snapshotFiles.length > 1) {
    result.conflicts.push({
      type: 'snapshot',
      description: 'Multiple ModelSnapshot files found',
      files: snapshotFiles.map(f => path.relative(structure.root, f)),
      resolution: 'Remove duplicate snapshots, keep only one per DbContext',
    });
  }

  // Check if snapshot references all migrations
  const snapshotContent = await readText(snapshotFiles[0]);

  for (const migration of result.migrations) {
    if (migration.context !== 'Unknown' && !snapshotContent.includes(migration.name)) {
      result.conflicts.push({
        type: 'dependency',
        description: `Migration "${migration.name}" not referenced in ModelSnapshot`,
        files: [migration.file],
        resolution: 'Migration may not be applied. Run: dotnet ef database update',
      });
    }
  }
}

function generateSuggestions(result: MigrationCheckResult): void {
  if (result.conflicts.some(c => c.type === 'snapshot')) {
    result.suggestions.push(
      'Consider rebasing on the target branch before merging to avoid snapshot conflicts'
    );
  }

  if (result.conflicts.some(c => c.type === 'naming')) {
    result.suggestions.push(
      'Use convention: {context}_v{version}_{sequence}_{Description} for migration naming (e.g., core_v1.0.0_001_CreateAuthUsers)'
    );
  }

  if (result.conflicts.some(c => c.type === 'order')) {
    result.suggestions.push(
      'Ensure migrations are created in version order to avoid conflicts'
    );
  }

  if (result.migrations.length > 20) {
    result.suggestions.push(
      'Consider squashing old migrations to reduce complexity. Use: /efcore squash'
    );
  }
}

function formatResult(
  result: MigrationCheckResult,
  currentBranch: string,
  compareBranch?: string
): string {
  const lines: string[] = [];

  lines.push('# EF Core Migration Check Report');
  lines.push('');
  lines.push('## Overview');
  lines.push(`- **Current Branch**: ${currentBranch}`);
  if (compareBranch) {
    lines.push(`- **Compare Branch**: ${compareBranch}`);
  }
  lines.push(`- **Total Migrations**: ${result.migrations.length}`);
  lines.push(`- **Conflicts Found**: ${result.conflicts.length}`);
  lines.push(`- **Status**: ${result.hasConflicts ? '❌ CONFLICTS DETECTED' : '✅ OK'}`);
  lines.push('');

  // Migrations list
  lines.push('## Migrations');
  lines.push('');
  lines.push('| Name | Context | Version | Sequence | Description |');
  lines.push('|------|---------|---------|----------|-------------|');

  for (const migration of result.migrations) {
    lines.push(
      `| ${migration.name} | ${migration.context} | ${migration.version} | ${migration.sequence} | ${migration.description} |`
    );
  }
  lines.push('');

  // Conflicts
  if (result.conflicts.length > 0) {
    lines.push('## Conflicts');
    lines.push('');

    for (const conflict of result.conflicts) {
      const icon = conflict.type === 'snapshot' ? '🔄' :
        conflict.type === 'order' ? '📅' :
          conflict.type === 'naming' ? '📝' : '⚠️';

      lines.push(`### ${icon} ${conflict.type.toUpperCase()}: ${conflict.description}`);
      if (conflict.files.length > 0) {
        lines.push(`- **Files**: ${conflict.files.map(f => `\`${f}\``).join(', ')}`);
      }
      lines.push(`- **Resolution**: ${conflict.resolution}`);
      lines.push('');
    }
  }

  // Suggestions
  if (result.suggestions.length > 0) {
    lines.push('## Suggestions');
    lines.push('');
    for (const suggestion of result.suggestions) {
      lines.push(`- 💡 ${suggestion}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}
